import { useEffect, useState } from "react";
import styled from "styled-components";

// import EditCategoryDialog from "./EditCategoryDialog";
import LabelSection from "src/components/core/LabelSection";
import { IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

import { Black, White } from "src/styles/color";
import { hexToRgba } from "src/common/color";

import { useDialog } from "src/context/DialogContext";

import { arrayMove } from "@dnd-kit/sortable";
import DraggableBoard, {
  DraggableItem,
} from "src/components/core/DraggableBoard";

import {
  getSuitableAreaRoom,
  moveSuitableAreaRoomPosition,
} from "src/api/landingPage";
import font from "src/styles/font";
import device from "src/styles/device";

import SuitableAreaRoomEditDialog from "./SuitableAreaRoomEditDialog";

const S = {};

S.BoxWrapper = styled.div`
  width: 100%;
  height: auto;
  padding: 32px;
  border-radius: 32px;
  background-color: ${White};
  box-shadow: 0px 4px 20px ${hexToRgba(Black, 0.1)};
  justify-content: center;
  align-items: center;
  margin-bottom: 32px;
`;

S.Item = styled(DraggableItem)`
  position: relative;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;

  @media ${device.tabletL} {
    width: 170px;
    height: 140px;
  }
`;

S.ItemLabel = styled.div`
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translate(-50%, 0);
  padding: 4px 16px;
  border-radius: 16px;
  background-color: ${White};
  width: max-content;
  min-width: 75px;
  text-align: center;
  font-size: ${font.size.md};
  font-weight: 500;

  @media ${device.tabletL} {
    font-size: ${font.size.xs};
  }
`;

S.Image = styled.div`
  width: 100%;
  height: 100%;
  background-image: url(${(props) => props.$productImg});
  background-size: cover;
  background-position: center;
`;

S.ItemListContainer = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

S.HoverContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  opacity: 0;
  transition: all 0.2s;

  &:hover {
    opacity: 1;
  }
`;

S.ItemBackdrop = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${hexToRgba(Black, 0.5)};
  border-radius: 16px;
`;

S.ItemActionMenu = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  padding: 8px;
`;

const SuitableAreaRoom = () => {
  const { openDialog, closeDialog, closeAllDialogs } = useDialog();

  const [suitableAreaRooms, setSuitableAreaRooms] = useState([]);

  const fetchSuitableAreaRooms = async () => {
    try {
      const result = await getSuitableAreaRoom();
      setSuitableAreaRooms(result);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchSuitableAreaRooms();
  }, []);

  return (
    <S.BoxWrapper>
      <LabelSection
        title="หาสินค้าที่ชอบจากพื้นที่ที่ใช่"
        description={`คุณสามารถแก้ไข้และสลับตำแหน่งได้`}
      >
        <DraggableBoard
          items={suitableAreaRooms}
          onDragEnd={async ({ active, over }) => {
            if (active.id !== over?.id) {
              const oldIndex = suitableAreaRooms.findIndex(
                (item) => item._id === active.id,
              );
              const newIndex = suitableAreaRooms.findIndex(
                (item) => item._id === over.id,
              );

              const newReArranged = arrayMove(
                suitableAreaRooms,
                oldIndex,
                newIndex,
              );

              setSuitableAreaRooms(newReArranged); // optimistic update

              try {
                await moveSuitableAreaRoomPosition(newReArranged);
              } catch (err) {
                setSuitableAreaRooms(suitableAreaRooms); // if error, revert to original order
                console.error(err);
              }
            }
          }}
          Components={{
            DroppableParent: ({ items, children }) => {
              return (
                <S.ItemListContainer>{children({ items })}</S.ItemListContainer>
              );
            },
            DraggableItem: (props) => {
              const { item, items, ...rest } = props; // Destructuring items to prevent this spreading to html element
              return (
                <S.Item {...rest}>
                  <S.Image $productImg={item?.img} />
                  <S.ItemLabel>{item?.room_th ?? item?.area_th}</S.ItemLabel>
                  <S.HoverContainer>
                    <S.ItemBackdrop />
                    <S.ItemActionMenu>
                      <IconButton
                        sx={{
                          color: White,
                          backgroundColor: hexToRgba(White, 0.4),
                        }}
                        onClick={() => {
                          openDialog({
                            type: "custom",
                            customDialog: (
                              <SuitableAreaRoomEditDialog
                                initialValue={item}
                                onClose={() => closeDialog()}
                                onSuccess={async () => {
                                  closeAllDialogs();
                                  await fetchSuitableAreaRooms();
                                }}
                              />
                            ),
                          });
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </S.ItemActionMenu>
                  </S.HoverContainer>
                </S.Item>
              );
            },
          }}
        />
      </LabelSection>
    </S.BoxWrapper>
  );
};

export default SuitableAreaRoom;
